import { ReactElement, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { usePrevious } from 'services/hooks';
import type { TagStore } from 'services/store/tags';
import { TagType } from 'services/api/responses/responseTypes';

import MobileTagsListContainer from '../MobileTagsList/MobileTagsListContainer';

type TagData = { type: TagType; name: string };

type PropsType = {
  tagStore: TagStore;
  getTagList: (tagType: TagType) => void;
};

const MobileTagDrawerList = (props: PropsType): ReactElement => {
  const { tagStore, getTagList } = props;
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState<string | boolean>(false);
  const [selectedTagType, setSelectedTagType] = useState(null);

  const previouslySelectedTagType = usePrevious(selectedTagType);

  const tagData: Array<TagData> = [
    { type: 'moods', name: t('tags:type:mood') },
    { type: 'genres', name: t('tags:type:genre') },
    { type: 'vocals', name: t('tags:type:vocal_plural') },
    {
      type: 'instruments',
      name: t('tags:type:instrument_plural'),
    },
    { type: 'keys', name: t('tags:type:key') },
    { type: 'energies', name: t('tags:type:energy') },
    { type: 'eras', name: t('tags:type:era') },
  ];

  const isTagTypeSelected = (tagType: TagType): boolean =>
    selectedTagType === tagType;

  const getTagListIfNeeded = (tagType: TagType) => {
    // if the tag array for the given tagType exist already
    // in the tags map we don't need to fetch it again
    if (tagStore[tagType] != null) return;
    getTagList(tagType);
  };

  const getCurrentTagType = (): TagType =>
    // @ts-ignore
    selectedTagType || previouslySelectedTagType;

  const selectTagType = (tagType: TagType) => {
    const isSelected = isTagTypeSelected(tagType);
    getCurrentTagType();
    getTagListIfNeeded(tagType);
    // @ts-ignore
    setSelectedTagType(isSelected ? null : tagType);
  };

  const handleChange =
    (panel: string, type: TagType) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      selectTagType(type);
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Box sx={{ pb: 10 }}>
      {tagData.map((data: TagData) => {
        const { name, type } = data;
        return (
          <Accordion
            key={name}
            sx={{
              background: 'transparent',
              boxShadow: 'none',
              my: 0.5,
              '&:before': {
                display: 'none',
              },
            }}
            data-testid="mobile-filters-list"
            expanded={expanded === name}
            onChange={handleChange(name, type)}
            disableGutters
          >
            <AccordionSummary
              sx={{ padding: 0 }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${name}-content`}
              id={`${name}-header`}
              data-testid={`mobile-filters-list-item-${type}`}
            >
              <Typography>{name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <MobileTagsListContainer tagType={getCurrentTagType()} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default MobileTagDrawerList;
